"use client";

import type React from "react";
import {
  FileText,
  Users,
  Shield,
  Database,
  MessageSquare,
  Eye,
  AlertTriangle,
  Scale,
  XCircle,
} from "lucide-react";

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-black text-white shadow-md">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-2xl md:text-3xl font-bold flex items-center">
            <FileText className="mr-2" />
            Terms and Agreement - SYNCAI.LTD
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-6 md:p-8">
          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-primary mb-4">
              Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              This document outlines the terms and agreement for the use of our
              Customer Relationship Management (CRM) tool, designed specifically
              for pharmaceutical medical sales representatives. By using this
              CRM tool, you agree to abide by the terms and conditions set forth
              herein. This agreement is intended to ensure the proper use of the
              tool for planning, reporting, storing customer data, engaging
              healthcare professionals, viewing activities of sales
              representatives, and allowing managers to monitor and track visits
              made by representatives.
            </p>
          </section>

          {/* Definitions */}
          <section className="mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-primary mb-4 flex items-center">
              <Users className="mr-2 text-primary" size={24} />
              1. Definitions
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>
                <span className="font-medium">CRM Tool:</span> The software
                application provided by our company for managing customer
                relationships and data.
              </li>
              <li>
                <span className="font-medium">User:</span> A pharmaceutical
                medical sales representative who has been granted access to the
                CRM tool.
              </li>
              <li>
                <span className="font-medium">Data:</span> Any information
                entered, stored, or processed within the CRM tool, including
                customer data and engagement content.
              </li>
              <li>
                <span className="font-medium">
                  Healthcare Professional (HCP):
                </span>{" "}
                A licensed individual or entity engaged in the delivery of
                healthcare services.
              </li>
              <li>
                <span className="font-medium">Manager:</span> An individual
                responsible for overseeing the activities of pharmaceutical
                medical sales representatives.
              </li>
            </ul>
          </section>

          {/* User Responsibilities */}
          <section className="mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-primary mb-4 flex items-center">
              <Shield className="mr-2 text-primary" size={24} />
              2. User Responsibilities
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>
                Users must use the CRM tool solely for planning, reporting,
                storing customer data, engaging healthcare professionals, and
                performing their duties as sales representatives.
              </li>
              <li>
                Users are responsible for maintaining the confidentiality and
                security of their login credentials and any data entered into
                the CRM tool.
              </li>
              <li>
                Users must comply with all applicable laws and regulations,
                including data protection and privacy laws, when using the CRM
                tool.
              </li>
              <li>
                Users must not share their login credentials or allow
                unauthorized individuals to access the CRM tool.
              </li>
            </ul>
          </section>

          {/* Data Management */}
          <section className="mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-primary mb-4 flex items-center">
              <Database className="mr-2 text-primary" size={24} />
              3. Data Management
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>
                Users must ensure the accuracy and completeness of any data
                entered into the CRM tool.
              </li>
              <li>
                Users are responsible for regularly updating and maintaining
                customer data within the CRM tool.
              </li>
              <li>
                Our company reserves the right to access, review, and manage
                data within the CRM tool for support and maintenance purposes.
              </li>
              <li>
                Users must report any data breaches or security incidents to our
                company immediately.
              </li>
            </ul>
          </section>

          {/* Engagement Content */}
          <section className="mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-primary mb-4 flex items-center">
              <MessageSquare className="mr-2 text-primary" size={24} />
              4. Engagement Content
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>
                Users may use the CRM tool to store and share content designed
                to engage healthcare professionals.
              </li>
              <li>
                All content must be professional, accurate, and comply with
                industry standards and regulations.
              </li>
              <li>
                Users must not upload or share content that is misleading,
                offensive, or violates any third-party rights.
              </li>
            </ul>
          </section>

          {/* Compliance and Monitoring */}
          <section className="mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-primary mb-4 flex items-center">
              <Eye className="mr-2 text-primary" size={24} />
              5. Compliance and Monitoring
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>
                Our company may monitor the use of the CRM tool to ensure
                compliance with this agreement and applicable laws.
              </li>
              <li>
                Users found to be in violation of this agreement may have their
                access to the CRM tool suspended or terminated.
              </li>
              <li>
                Managers may use the CRM tool to view and track the activities
                of pharmaceutical medical sales representatives, including
                monitoring and tracking visits made by representatives.
              </li>
              <li>
                Our company reserves the right to modify or update this
                agreement at any time, with or without notice to users.
              </li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-primary mb-4 flex items-center">
              <AlertTriangle className="mr-2 text-primary" size={24} />
              6. Limitation of Liability
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>
                Our company is not liable for any loss or damage arising from
                the use or inability to use the CRM tool.
              </li>
              <li>
                Users agree to indemnify and hold our company harmless from any
                claims, liabilities, or damages resulting from their use of the
                CRM tool.
              </li>
            </ul>
          </section>

          {/* Governing Law */}
          <section className="mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-primary mb-4 flex items-center">
              <Scale className="mr-2 text-primary" size={24} />
              7. Governing Law
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>
                This agreement is governed by the laws of the jurisdiction in
                which our company is based.
              </li>
              <li>
                Any disputes arising from this agreement will be resolved in
                accordance with the legal procedures of the governing
                jurisdiction.
              </li>
            </ul>
          </section>

          {/* Termination */}
          <section className="mb-8">
            <h2 className="text-xl md:text-2xl font-semibold text-primary mb-4 flex items-center">
              <XCircle className="mr-2 text-primary" size={24} />
              8. Termination
            </h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>
                Users may terminate their use of the CRM tool at any time by
                notifying our company.
              </li>
              <li>
                Our company reserves the right to terminate a user's access to
                the CRM tool for any reason, including violation of this
                agreement.
              </li>
              <li>
                Upon termination, users must cease all use of the CRM tool and
                delete any data obtained through the tool.
              </li>
            </ul>
          </section>

          {/* Conclusion */}
          <section className="mt-10 border-t pt-6 border-gray-200">
            <h2 className="text-xl md:text-2xl font-semibold text-primary mb-4">
              Conclusion
            </h2>
            <p className="text-gray-700 leading-relaxed">
              By using our CRM tool, you acknowledge that you have read,
              understood, and agreed to these terms and conditions. If you have
              any questions or concerns about this agreement.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6 mt-10">
        <div className="container mx-auto px-4 text-center">
          <p>© {new Date().getFullYear()} SYNCAI.LTD. All rights reserved.</p>
          <p className="mt-2 text-gray-400">
            Customer Relationship Management Tool for Pharmaceutical Medical
            Sales Representatives
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
